import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Clock, Copy, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import type { NotificationAction, NotificationAudience, NotificationCampaignDraftRequest, NotificationRecipient } from "@osmix/contracts/notification";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import type { NotificationCampaign } from "@osmix/contracts/notification";
import { useCampaign, useCampaignActions, useCampaignStats, useCampaigns, useRecipientSearch, useWeeklyDigestSettings } from "@/api/hooks/use-notifications";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
const TERMINAL = new Set(["sent", "cancelled", "suppressed", "failed"]);

// Кампания «запланирована», если её availableAt в будущем и она ещё не в
// терминальном статусе — рассылка сработает по времени (availableAt) на бэкенде.
const scheduledDate = (campaign: NotificationCampaign): Date | null => {
  if (TERMINAL.has(campaign.status)) return null;
  const at = new Date(campaign.availableAt);
  return at.getTime() > Date.now() ? at : null;
};
const CAMPAIGN_STATUSES = ["all", "draft", "queued", "processing", "scheduled", "sent", "cancelled", "failed"] as const;
const CATEGORIES = ["system", "trade", "marketing", "social", "financial", "analytics"] as const;
const ACTION_KINDS = ["none", "order", "product", "shop", "chat", "profile"] as const;
const SELECTED_PAGE_SIZE = 5;

// «Похожие подборки недели» (fd-notif-01): автоматическая рассылка площадки.
// Включается здесь; получают её покупатели, включившие подборки в своих
// настройках уведомлений. Содержание персональное — по профилю интересов.
function WeeklyDigestCard() {
  const $ = _c(63);
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp);
  const canEdit = hasAdminPermission(user, "action.notifications.broadcast");
  const {
    query,
    save
  } = useWeeklyDigestSettings();
  const [cadence, setCadence] = useState(null);
  const data = query.data;
  if (!data) {
    return null;
  }
  const days = cadence ?? String(data.cadenceDays);
  const daysValid = /^\d+$/.test(days) && Number(days) >= 1 && Number(days) <= 90;
  let t0;
  if ($[0] !== t) {
    t0 = t("notificationCampaigns.weeklyDigest.title");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <CardTitle>{t0}</CardTitle>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("notificationCampaigns.weeklyDigest.hint");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <p className="text-sm text-muted-foreground">{t2}</p>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <CardHeader>{t1}{t3}</CardHeader>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = !canEdit || save.isPending;
  let t6;
  if ($[11] !== data || $[12] !== save) {
    t6 = event => save.mutate({
      ...data,
      enabled: event.target.checked
    });
    $[11] = data;
    $[12] = save;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== data.enabled || $[15] !== t5 || $[16] !== t6) {
    t7 = <input type="checkbox" className="size-4 accent-primary" checked={data.enabled} disabled={t5} onChange={t6} />;
    $[14] = data.enabled;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("notificationCampaigns.weeklyDigest.enabled");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <label className="flex items-center gap-2 text-sm">{t7}{t8}</label>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("notificationCampaigns.weeklyDigest.cadence");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <Label htmlFor="weekly-digest-cadence">{t10}</Label>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = !canEdit || save.isPending;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event_0 => setCadence(event_0.target.value.replace(/\D/g, "").slice(0, 2));
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== days || $[29] !== t12) {
    t14 = <Input id="weekly-digest-cadence" className="w-24" inputMode="numeric" value={days} disabled={t12} onChange={t13} />;
    $[28] = days;
    $[29] = t12;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = !canEdit || save.isPending || !daysValid || Number(days) === data.cadenceDays;
  let t16;
  if ($[31] !== data || $[32] !== days || $[33] !== save) {
    t16 = () => save.mutate({
      ...data,
      cadenceDays: Number(days)
    }, {
      onSuccess: () => setCadence(null)
    });
    $[31] = data;
    $[32] = days;
    $[33] = save;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t) {
    t17 = t("notificationCampaigns.weeklyDigest.save");
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <Button variant="outline" size="sm" disabled={t15} onClick={t16}>{t17}</Button>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t14 || $[42] !== t18) {
    t19 = <div className="flex items-center gap-2">{t14}{t18}</div>;
    $[41] = t14;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t11 || $[45] !== t19) {
    t20 = <div className="grid gap-1">{t11}{t19}</div>;
    $[44] = t11;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const t21 = data.enabled ? "default" : "secondary";
  let t22;
  if ($[47] !== data.enabled || $[48] !== t) {
    t22 = data.enabled ? t("notificationCampaigns.weeklyDigest.on") : t("notificationCampaigns.weeklyDigest.off");
    $[47] = data.enabled;
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t21 || $[51] !== t22) {
    t23 = <Badge variant={t21}>{t22}</Badge>;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== save.error) {
    t24 = save.error && <p className="w-full text-sm text-destructive">{save.error.message}</p>;
    $[53] = save.error;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t20 || $[56] !== t23 || $[57] !== t24 || $[58] !== t9) {
    t25 = <CardContent className="flex flex-wrap items-end gap-4">{t9}{t20}{t23}{t24}</CardContent>;
    $[55] = t20;
    $[56] = t23;
    $[57] = t24;
    $[58] = t9;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t25 || $[61] !== t4) {
    t26 = <Card>{t4}{t25}</Card>;
    $[60] = t25;
    $[61] = t4;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  return t26;
}
function _temp(state) {
  return state.user;
}
export function NotificationCampaignsPage() {
  const $ = _c(121);
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp2);
  const [status, setStatus] = useState("all");
  const query = useCampaigns(status);
  let T0;
  let T1;
  let T2;
  let t0;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== query.data?.pages || $[1] !== query.error || $[2] !== query.isPending || $[3] !== status || $[4] !== t || $[5] !== user) {
    const campaigns = query.data?.pages.flatMap(_temp3) ?? [];
    let t11;
    if ($[20] !== user) {
      t11 = hasAdminPermission(user, "action.notifications.manage") || hasAdminPermission(user, "action.notifications.broadcast");
      $[20] = user;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    const canCreate = t11;
    t6 = "grid gap-5";
    let t12;
    if ($[22] !== t) {
      t12 = t("notificationCampaigns.title");
      $[22] = t;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== t12) {
      t13 = <h1 className="text-2xl font-semibold tracking-tight">{t12}</h1>;
      $[24] = t12;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== t) {
      t14 = t("notificationCampaigns.description");
      $[26] = t;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== t14) {
      t15 = <p className="text-sm text-muted-foreground">{t14}</p>;
      $[28] = t14;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    let t16;
    if ($[30] !== t13 || $[31] !== t15) {
      t16 = <div data-tour="campaigns-intro">{t13}{t15}</div>;
      $[30] = t13;
      $[31] = t15;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    let t17;
    if ($[33] !== canCreate || $[34] !== t) {
      t17 = canCreate && <span data-tour="campaigns-create"><Button asChild={true}><Link to="/notification-campaigns/new"><Plus className="size-4" />{t("notificationCampaigns.create")}</Link></Button></span>;
      $[33] = canCreate;
      $[34] = t;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    if ($[36] !== t16 || $[37] !== t17) {
      t7 = <div className="flex flex-wrap items-center justify-between gap-3">{t16}{t17}</div>;
      $[36] = t16;
      $[37] = t17;
      $[38] = t7;
    } else {
      t7 = $[38];
    }
    let t18;
    if ($[39] !== t) {
      t18 = t("notificationCampaigns.status");
      $[39] = t;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    let t19;
    if ($[41] !== t18) {
      t19 = <Label htmlFor="campaign-status">{t18}</Label>;
      $[41] = t18;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    let t20;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = event => setStatus(event.target.value);
      $[43] = t20;
    } else {
      t20 = $[43];
    }
    let t21;
    if ($[44] !== t) {
      t21 = CAMPAIGN_STATUSES.map(value => <option key={value} value={value}>{t(`notificationCampaigns.statuses.${value}`)}</option>);
      $[44] = t;
      $[45] = t21;
    } else {
      t21 = $[45];
    }
    let t22;
    if ($[46] !== status || $[47] !== t21) {
      t22 = <select id="campaign-status" className="h-9 rounded-md border bg-background px-3 text-sm" value={status} onChange={t20}>{t21}</select>;
      $[46] = status;
      $[47] = t21;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    if ($[49] !== t19 || $[50] !== t22) {
      t8 = <div data-tour="campaigns-filter" className="flex items-center gap-2">{t19}{t22}</div>;
      $[49] = t19;
      $[50] = t22;
      $[51] = t8;
    } else {
      t8 = $[51];
    }
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <WeeklyDigestCard />;
      $[52] = t9;
    } else {
      t9 = $[52];
    }
    if ($[53] !== query.error) {
      t10 = query.error && <div className="rounded-md border border-destructive/40 p-4 text-destructive">{query.error.message}</div>;
      $[53] = query.error;
      $[54] = t10;
    } else {
      t10 = $[54];
    }
    T2 = Card;
    t4 = "campaigns-table";
    t5 = "overflow-hidden py-0";
    T1 = Table;
    let t23;
    if ($[55] !== t) {
      t23 = t("notificationCampaigns.campaign");
      $[55] = t;
      $[56] = t23;
    } else {
      t23 = $[56];
    }
    let t24;
    if ($[57] !== t23) {
      t24 = <TableHead className="min-w-[300px]">{t23}</TableHead>;
      $[57] = t23;
      $[58] = t24;
    } else {
      t24 = $[58];
    }
    let t25;
    if ($[59] !== t) {
      t25 = t("notificationCampaigns.status");
      $[59] = t;
      $[60] = t25;
    } else {
      t25 = $[60];
    }
    let t26;
    if ($[61] !== t25) {
      t26 = <TableHead>{t25}</TableHead>;
      $[61] = t25;
      $[62] = t26;
    } else {
      t26 = $[62];
    }
    let t27;
    if ($[63] !== t) {
      t27 = t("notificationCampaigns.audience");
      $[63] = t;
      $[64] = t27;
    } else {
      t27 = $[64];
    }
    let t28;
    if ($[65] !== t27) {
      t28 = <TableHead>{t27}</TableHead>;
      $[65] = t27;
      $[66] = t28;
    } else {
      t28 = $[66];
    }
    let t29;
    if ($[67] !== t) {
      t29 = t("notificationCampaigns.targeted");
      $[67] = t;
      $[68] = t29;
    } else {
      t29 = $[68];
    }
    let t30;
    if ($[69] !== t29) {
      t30 = <TableHead className="text-right">{t29}</TableHead>;
      $[69] = t29;
      $[70] = t30;
    } else {
      t30 = $[70];
    }
    let t31;
    if ($[71] !== t) {
      t31 = t("notificationCampaigns.read");
      $[71] = t;
      $[72] = t31;
    } else {
      t31 = $[72];
    }
    let t32;
    if ($[73] !== t31) {
      t32 = <TableHead className="text-right">{t31}</TableHead>;
      $[73] = t31;
      $[74] = t32;
    } else {
      t32 = $[74];
    }
    let t33;
    if ($[75] !== t) {
      t33 = t("notificationCampaigns.unread");
      $[75] = t;
      $[76] = t33;
    } else {
      t33 = $[76];
    }
    let t34;
    if ($[77] !== t33) {
      t34 = <TableHead className="text-right">{t33}</TableHead>;
      $[77] = t33;
      $[78] = t34;
    } else {
      t34 = $[78];
    }
    let t35;
    if ($[79] !== t) {
      t35 = t("notificationCampaigns.created");
      $[79] = t;
      $[80] = t35;
    } else {
      t35 = $[80];
    }
    let t36;
    if ($[81] !== t35) {
      t36 = <TableHead>{t35}</TableHead>;
      $[81] = t35;
      $[82] = t36;
    } else {
      t36 = $[82];
    }
    if ($[83] !== t24 || $[84] !== t26 || $[85] !== t28 || $[86] !== t30 || $[87] !== t32 || $[88] !== t34 || $[89] !== t36) {
      t3 = <TableHeader className="bg-muted/40"><TableRow>{t24}{t26}{t28}{t30}{t32}{t34}{t36}</TableRow></TableHeader>;
      $[83] = t24;
      $[84] = t26;
      $[85] = t28;
      $[86] = t30;
      $[87] = t32;
      $[88] = t34;
      $[89] = t36;
      $[90] = t3;
    } else {
      t3 = $[90];
    }
    T0 = TableBody;
    if ($[91] !== query.isPending || $[92] !== t) {
      t0 = query.isPending && <TableRow><TableCell colSpan={7} className="h-28 text-center text-muted-foreground">{t("notificationCampaigns.loading")}</TableCell></TableRow>;
      $[91] = query.isPending;
      $[92] = t;
      $[93] = t0;
    } else {
      t0 = $[93];
    }
    t1 = !query.isPending && campaigns.length === 0 && <TableRow><TableCell colSpan={7} className="h-28 text-center text-muted-foreground">{t("notificationCampaigns.empty")}</TableCell></TableRow>;
    let t37;
    if ($[94] !== t) {
      t37 = campaign => <TableRow key={campaign.id}><TableCell className="whitespace-normal py-3"><Link to={`/notification-campaigns/${campaign.id}`} className="block rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"><span className="block font-medium text-foreground hover:underline">{campaign.content.ru.title}</span><span className="mt-0.5 block max-w-xl truncate text-xs text-muted-foreground">{campaign.content.ru.body}</span></Link></TableCell><TableCell>{scheduledDate(campaign) ? <div className="flex flex-col gap-0.5"><Badge variant="outline" className="w-fit gap-1 text-amber-600 dark:text-amber-500"><Clock className="size-3" />{t("notificationCampaigns.scheduled")}</Badge><time dateTime={campaign.availableAt} className="text-xs text-muted-foreground">{new Date(campaign.availableAt).toLocaleString()}</time></div> : <Badge variant="secondary">{t(`notificationCampaigns.statuses.${campaign.status}`)}</Badge>}</TableCell><TableCell>{t(`notificationCampaigns.audiences.${campaign.audience.kind}`)}</TableCell><TableCell className="text-right tabular-nums">{campaign.stats.targeted}</TableCell><TableCell className="text-right tabular-nums">{campaign.stats.read}</TableCell><TableCell className="text-right tabular-nums">{campaign.stats.unread}</TableCell><TableCell className="text-muted-foreground"><time dateTime={campaign.createdAt}>{new Date(campaign.createdAt).toLocaleString()}</time></TableCell></TableRow>;
      $[94] = t;
      $[95] = t37;
    } else {
      t37 = $[95];
    }
    t2 = campaigns.map(t37);
    $[0] = query.data?.pages;
    $[1] = query.error;
    $[2] = query.isPending;
    $[3] = status;
    $[4] = t;
    $[5] = user;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = t0;
    $[10] = t1;
    $[11] = t10;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    t0 = $[9];
    t1 = $[10];
    t10 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[96] !== T0 || $[97] !== t0 || $[98] !== t1 || $[99] !== t2) {
    t11 = <T0>{t0}{t1}{t2}</T0>;
    $[96] = T0;
    $[97] = t0;
    $[98] = t1;
    $[99] = t2;
    $[100] = t11;
  } else {
    t11 = $[100];
  }
  let t12;
  if ($[101] !== T1 || $[102] !== t11 || $[103] !== t3) {
    t12 = <T1>{t3}{t11}</T1>;
    $[101] = T1;
    $[102] = t11;
    $[103] = t3;
    $[104] = t12;
  } else {
    t12 = $[104];
  }
  let t13;
  if ($[105] !== T2 || $[106] !== t12 || $[107] !== t4 || $[108] !== t5) {
    t13 = <T2 data-tour={t4} className={t5}>{t12}</T2>;
    $[105] = T2;
    $[106] = t12;
    $[107] = t4;
    $[108] = t5;
    $[109] = t13;
  } else {
    t13 = $[109];
  }
  let t14;
  if ($[110] !== query || $[111] !== t) {
    t14 = query.hasNextPage && <Button variant="outline" className="mx-auto" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>{query.isFetchingNextPage ? t("notificationCampaigns.loading") : t("notificationCampaigns.loadMore")}</Button>;
    $[110] = query;
    $[111] = t;
    $[112] = t14;
  } else {
    t14 = $[112];
  }
  let t15;
  if ($[113] !== t10 || $[114] !== t13 || $[115] !== t14 || $[116] !== t6 || $[117] !== t7 || $[118] !== t8 || $[119] !== t9) {
    t15 = <div className={t6}>{t7}{t8}{t9}{t10}{t13}{t14}</div>;
    $[113] = t10;
    $[114] = t13;
    $[115] = t14;
    $[116] = t6;
    $[117] = t7;
    $[118] = t8;
    $[119] = t9;
    $[120] = t15;
  } else {
    t15 = $[120];
  }
  return t15;
}
function _temp3(page) {
  return page.items;
}
function _temp2(state) {
  return state.user;
}
export function NotificationCampaignDetailPage() {
  const $ = _c(22);
  const {
    t
  } = useTranslation();
  const {
    id
  } = useParams();
  const user = useAuth(_temp4);
  const query = useCampaign(id);
  const statsQuery = useCampaignStats(id);
  const actions = useCampaignActions();
  const campaign = query.data;
  let t0;
  if ($[0] !== actions || $[1] !== campaign || $[2] !== query.error || $[3] !== query.isPending || $[4] !== statsQuery || $[5] !== t || $[6] !== user) {
    const scheduledAt = campaign ? scheduledDate(campaign) : null;
    const permission = campaign?.audience.kind === "individual" ? "action.notifications.manage" : "action.notifications.broadcast";
    let t1;
    if ($[8] !== campaign || $[9] !== permission || $[10] !== user) {
      t1 = campaign && !TERMINAL.has(campaign.status) && hasAdminPermission(user, permission);
      $[8] = campaign;
      $[9] = permission;
      $[10] = user;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    const canCancel = Boolean(t1);
    let t2;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ArrowLeft className="size-4" />;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== t) {
      t3 = t("notificationCampaigns.back");
      $[13] = t;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== t3) {
      t4 = <Button asChild={true} variant="ghost" className="w-fit"><Link to="/notification-campaigns">{t2}{t3}</Link></Button>;
      $[15] = t3;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== query.isPending || $[18] !== t) {
      t5 = query.isPending && <p>{t("notificationCampaigns.loading")}</p>;
      $[17] = query.isPending;
      $[18] = t;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== query.error) {
      t6 = query.error && <div className="text-destructive">{query.error.message}</div>;
      $[20] = query.error;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    t0 = <div className="mx-auto grid max-w-4xl gap-4">{t4}{t5}{t6}{campaign && <><div className="flex flex-wrap items-start justify-between gap-3"><div><h1 className="text-2xl font-bold">{campaign.content.ru.title}</h1><p className="text-sm text-muted-foreground">{campaign.id}</p></div>{scheduledAt ? <div className="flex flex-col items-end gap-0.5"><Badge variant="outline" className="gap-1 text-amber-600 dark:text-amber-500"><Clock className="size-3.5" />{t("notificationCampaigns.scheduled")}</Badge><time dateTime={campaign.availableAt} className="text-xs text-muted-foreground">{scheduledAt.toLocaleString()}</time></div> : <Badge>{t(`notificationCampaigns.statuses.${campaign.status}`)}</Badge>}</div><Card><CardHeader><CardTitle>{t("notificationCampaigns.content")}</CardTitle></CardHeader><CardContent className="grid gap-4">{(["ru", "en", "hy"] as const).map(locale => {
              const content = campaign.content[locale];
              return content ? <div key={locale} className="rounded-md border p-3">{Object.keys(campaign.content).length > 1 && <strong className="uppercase">{locale}</strong>}<p className="font-medium">{content.title}</p><p className="whitespace-pre-wrap text-sm text-muted-foreground">{content.body}</p></div> : null;
            })}</CardContent></Card><Card><CardHeader><CardTitle>{t("notificationCampaigns.stats")}</CardTitle></CardHeader><CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-5">{Object.entries(campaign.stats).map(t7 => {
              const [key, value] = t7;
              return <div key={key}><p className="text-2xl font-bold">{value}</p><p className="text-xs text-muted-foreground">{t(`notificationCampaigns.${key}`)}</p></div>;
            })}</CardContent></Card><Card><CardHeader><CardTitle>{t("notificationCampaigns.deliveryStats")}</CardTitle></CardHeader><CardContent className="grid gap-4">{statsQuery.isPending && <p className="text-sm text-muted-foreground">{t("notificationCampaigns.loading")}</p>}{statsQuery.error && <p className="text-sm text-destructive">{statsQuery.error.message}</p>}{statsQuery.data && <><div className="grid grid-cols-2 gap-4 sm:grid-cols-4"><div><p className="text-2xl font-bold tabular-nums">{statsQuery.data.recipients}</p><p className="text-xs text-muted-foreground">{t("notificationCampaigns.stat.recipients")}</p></div><div><p className="text-2xl font-bold tabular-nums">{statsQuery.data.delivered}</p><p className="text-xs text-muted-foreground">{t("notificationCampaigns.stat.delivered")}</p></div><div><p className="text-2xl font-bold tabular-nums">{statsQuery.data.read}</p><p className="text-xs text-muted-foreground">{t("notificationCampaigns.stat.read")}</p></div><div><p className="text-2xl font-bold tabular-nums">{Math.round(statsQuery.data.readRate * 100)}%</p><p className="text-xs text-muted-foreground">{t("notificationCampaigns.stat.readRate")}</p></div></div>{statsQuery.data.byTime.length > 0 && <div className="overflow-hidden rounded-lg border"><Table><TableHeader className="bg-muted/40"><TableRow><TableHead>{t("notificationCampaigns.stat.day")}</TableHead><TableHead className="text-right">{t("notificationCampaigns.stat.delivered")}</TableHead><TableHead className="text-right">{t("notificationCampaigns.stat.read")}</TableHead></TableRow></TableHeader><TableBody>{statsQuery.data.byTime.map(_temp5)}</TableBody></Table></div>}</>}</CardContent></Card>{campaign.statusReason && <div className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">{campaign.statusReason}</div>}{canCancel && <Button variant="destructive" className="w-fit" disabled={actions.cancel.isPending} onClick={() => actions.cancel.mutate(campaign.id)}>{t("notificationCampaigns.cancel")}</Button>}</>}</div>;
    $[0] = actions;
    $[1] = campaign;
    $[2] = query.error;
    $[3] = query.isPending;
    $[4] = statsQuery;
    $[5] = t;
    $[6] = user;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
}
function _temp5(row) {
  return <TableRow key={row.day}><TableCell>{row.day}</TableCell><TableCell className="text-right tabular-nums">{row.delivered}</TableCell><TableCell className="text-right tabular-nums">{row.read}</TableCell></TableRow>;
}
function _temp4(state) {
  return state.user;
}
type FormState = {
  audience: "individual" | "all_users" | "all_buyers" | "all_sellers";
  category: "system" | "trade" | "marketing" | "social" | "financial" | "analytics";
  ruTitle: string;
  ruBody: string;
  enTitle: string;
  enBody: string;
  hyTitle: string;
  hyBody: string;
  actionKind: "none" | "order" | "product" | "shop" | "chat" | "profile";
  actionTarget: string;
  // Расписание: «сейчас» ставит рассылку в очередь немедленно, «запланировать»
  // откладывает до scheduledAt. Значение датапикера — стенные часы МСК.
  schedule: "now" | "scheduled";
  scheduledAt: string;
};

// Пользователь вводит время в формате datetime-local (YYYY-MM-DDTHH:mm) без
// зоны. Консоль трактует его как МСК (UTC+3): дописываем смещение и приводим к
// ISO в UTC для availableAt, который ждёт бэкенд.
const mskToIso = (local: string): string | null => {
  if (!local) return null;
  const date = new Date(`${local}:00+03:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

// Текущее стенное время МСК в формате datetime-local — для атрибута min и
// значения по умолчанию (sv-SE даёт ISO-подобную запись «YYYY-MM-DD HH:mm:ss»).
const mskNowLocal = (): string => new Date().toLocaleString("sv-SE", {
  timeZone: "Europe/Moscow"
}).replace(" ", "T").slice(0, 16);

// Человекочитаемая подпись запланированного времени: ДД.ММ.ГГГГ ЧЧ:ММ (МСК).
const formatMskLabel = (local: string): string => {
  const [date, time] = local.split("T");
  if (!date || !time) return local;
  const [year, month, day] = date.split("-");
  return `${day}.${month}.${year} ${time} (МСК)`;
};
type ContentLocale = "ru" | "en" | "hy";
/** Языки, на которых оператор пишет рассылку. Сейчас только русский. */
const EDITABLE_LOCALES: ContentLocale[] = ["ru"];
type CampaignPreview = Awaited<ReturnType<typeof import("@/api").api.notificationCampaigns.preview>>;
type DraftRecord = {
  id: string;
  recipient?: NotificationRecipient;
};
type PreviewRecord = DraftRecord & {
  preview: CampaignPreview;
};
const CONTENT_FIELDS = {
  ru: {
    title: "ruTitle",
    body: "ruBody"
  },
  en: {
    title: "enTitle",
    body: "enBody"
  },
  hy: {
    title: "hyTitle",
    body: "hyBody"
  }
} as const;
const initial: FormState = {
  audience: "individual",
  category: "system",
  ruTitle: "",
  ruBody: "",
  enTitle: "",
  enBody: "",
  hyTitle: "",
  hyBody: "",
  actionKind: "none",
  actionTarget: "",
  schedule: "now",
  scheduledAt: ""
};
export function NotificationCampaignComposePage() {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const user = useAuth(state => state.user);
  const [form, setForm] = useState(initial);
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<NotificationRecipient[]>([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [activeLocale, setActiveLocale] = useState<ContentLocale>("ru");
  const [drafts, setDrafts] = useState<DraftRecord[]>([]);
  const [previews, setPreviews] = useState<PreviewRecord[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recipients = useRecipientSearch(search.trim());
  const actions = useCampaignActions();
  const canBroadcast = hasAdminPermission(user, "action.notifications.broadcast");
  const resetPreparedState = () => {
    setDrafts([]);
    setPreviews([]);
    setConfirmOpen(false);
  };
  const set = <K extends keyof FormState,>(key: K, value: FormState[K]) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    resetPreparedState();
  };
  const action = useMemo<NotificationAction | undefined>(() => {
    if (form.actionKind === "none") return undefined;
    if (form.actionKind === "profile") return {
      kind: "profile"
    };
    if (form.actionKind === "order") return {
      kind: "order",
      orderId: form.actionTarget
    };
    if (form.actionKind === "product") return {
      kind: "product",
      productId: form.actionTarget
    };
    if (form.actionKind === "shop") return {
      kind: "shop",
      shopSlug: form.actionTarget
    };
    return {
      kind: "chat",
      conversationId: form.actionTarget
    };
  }, [form.actionKind, form.actionTarget]);
  const audienceFor = (recipient?: NotificationRecipient): NotificationAudience => form.audience === "individual" ? {
    kind: "individual",
    userId: recipient?.id ?? ""
  } : {
    kind: form.audience
  };
  const scheduledIso = form.schedule === "scheduled" ? mskToIso(form.scheduledAt) : null;
  const scheduleInFuture = scheduledIso !== null && new Date(scheduledIso).getTime() > Date.now();
  const scheduleValid = form.schedule === "now" || scheduleInFuture;
  const payload = (recipient_0?: NotificationRecipient): NotificationCampaignDraftRequest => ({
    content: {
      ru: {
        title: form.ruTitle,
        body: form.ruBody
      },
      ...(form.enTitle && form.enBody ? {
        en: {
          title: form.enTitle,
          body: form.enBody
        }
      } : {}),
      ...(form.hyTitle && form.hyBody ? {
        hy: {
          title: form.hyTitle,
          body: form.hyBody
        }
      } : {})
    },
    category: form.category,
    audience: audienceFor(recipient_0),
    action,
    data: {},
    ...(scheduledIso ? {
      availableAt: scheduledIso
    } : {})
  });
  const valid = Boolean(form.ruTitle.trim() && form.ruBody.trim() && (form.audience !== "individual" || selectedRecipients.length > 0) && (form.actionKind === "none" || form.actionKind === "profile" || form.actionTarget.trim()) && scheduleValid);
  const selectedPageCount = Math.max(1, Math.ceil(selectedRecipients.length / SELECTED_PAGE_SIZE));
  const selectedRows = selectedRecipients.slice((selectedPage - 1) * SELECTED_PAGE_SIZE, selectedPage * SELECTED_PAGE_SIZE);
  const suggestionsOpen = searchFocused && search.trim().length >= 2;
  const selectedIds = new Set(selectedRecipients.map(recipient_1 => recipient_1.id));
  const fields = CONTENT_FIELDS[activeLocale];
  const activeTitle = form[fields.title];
  const activeBody = form[fields.body];
  const previewTitle = activeTitle.trim() || (activeLocale === "ru" ? "" : form.ruTitle.trim()) || t("notificationCampaigns.previewTitlePlaceholder");
  const previewBody = activeBody.trim() || (activeLocale === "ru" ? "" : form.ruBody.trim()) || t("notificationCampaigns.previewBodyPlaceholder");
  const eligibleCount = previews.reduce((total, record) => total + record.preview.eligibleCount, 0);
  const busy = actions.create.isPending || actions.preview.isPending || actions.send.isPending;
  const addRecipient = (recipient_2: NotificationRecipient) => {
    if (selectedIds.has(recipient_2.id)) return;
    const next = [...selectedRecipients, recipient_2];
    setSelectedRecipients(next);
    setSelectedPage(Math.ceil(next.length / SELECTED_PAGE_SIZE));
    setSearch("");
    setSearchFocused(false);
    resetPreparedState();
  };
  const removeRecipient = (id: string) => {
    const next_0 = selectedRecipients.filter(recipient_3 => recipient_3.id !== id);
    setSelectedRecipients(next_0);
    setSelectedPage(Math.min(selectedPage, Math.max(1, Math.ceil(next_0.length / SELECTED_PAGE_SIZE))));
    resetPreparedState();
  };
  const copyRussian = () => {
    if (activeLocale === "ru") return;
    setForm(current_0 => ({
      ...current_0,
      [fields.title]: current_0.ruTitle,
      [fields.body]: current_0.ruBody
    }));
    resetPreparedState();
  };
  const clearTranslation = () => {
    if (activeLocale === "ru") return;
    setForm(current_1 => ({
      ...current_1,
      [fields.title]: "",
      [fields.body]: ""
    }));
    resetPreparedState();
  };
  const createDrafts = async (): Promise<DraftRecord[] | null> => {
    if (drafts.length > 0) return drafts;
    setError(null);
    try {
      const targets = form.audience === "individual" ? selectedRecipients : [undefined];
      const created: DraftRecord[] = [];
      for (const recipient_4 of targets) {
        const draft = await actions.create.mutateAsync(payload(recipient_4));
        created.push({
          id: draft.id,
          recipient: recipient_4
        });
      }
      setDrafts(created);
      setPreviews([]);
      return created;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      return null;
    }
  };
  const runPreview = async () => {
    const prepared = await createDrafts();
    if (!prepared) return;
    setError(null);
    try {
      const next_1: PreviewRecord[] = [];
      for (const draft_0 of prepared) {
        next_1.push({
          ...draft_0,
          preview: await actions.preview.mutateAsync({
            id: draft_0.id,
            locale: "ru"
          })
        });
      }
      setPreviews(next_1);
      setConfirmOpen(true);
    } catch (cause_0) {
      setError(cause_0 instanceof Error ? cause_0.message : String(cause_0));
    }
  };
  const send = async () => {
    if (previews.length === 0) return;
    setError(null);
    try {
      const sent = [];
      for (const record_0 of previews) {
        sent.push(await actions.send.mutateAsync({
          id: record_0.id,
          input: {
            previewToken: record_0.preview.previewToken,
            idempotencyKey: `admin-${record_0.id}`,
            confirmation: form.audience === "individual" ? undefined : `SEND ${eligibleCount}`
          }
        }));
      }
      const onlyCampaign = sent.length === 1 ? sent[0] : undefined;
      void navigate(onlyCampaign ? `/notification-campaigns/${onlyCampaign.id}` : "/notification-campaigns");
    } catch (cause_1) {
      setError(cause_1 instanceof Error ? cause_1.message : String(cause_1));
    }
  };
  return <div className="mx-auto grid max-w-5xl gap-5">
			<Button asChild variant="ghost" className="w-fit"><Link to="/notification-campaigns"><ArrowLeft className="size-4" />{t("notificationCampaigns.back")}</Link></Button>
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">{t("notificationCampaigns.compose")}</h1>
				<p className="text-sm text-muted-foreground">{t("notificationCampaigns.ruRequired")}</p>
			</div>
			{error && <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>}

			<Card>
				<CardHeader><CardTitle>{t("notificationCampaigns.audience")}</CardTitle></CardHeader>
				<CardContent className="grid gap-5">
					<div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
						{(["individual", "all_users", "all_sellers", "all_buyers"] as const).map(kind => <label key={kind} className={`flex min-h-12 cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${form.audience === kind ? "border-primary bg-primary/5" : "hover:bg-muted/40"}`}>
								<input type="radio" checked={form.audience === kind} disabled={kind !== "individual" && !canBroadcast} onChange={() => set("audience", kind)} />
								<span className="text-sm font-medium">{t(`notificationCampaigns.audiences.${kind}`)}</span>
							</label>)}
					</div>

					{form.audience === "individual" && <div className="grid gap-4">
							<div className="relative max-w-2xl">
								<Label htmlFor="recipient-search">{t("notificationCampaigns.searchRecipient")}</Label>
								<div className="relative mt-1.5">
									<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
									<Input id="recipient-search" value={search} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} onKeyDown={event => event.key === "Escape" && setSearchFocused(false)} onChange={event_0 => setSearch(event_0.target.value)} placeholder={t("notificationCampaigns.searchRecipientPlaceholder")} className="pl-9" aria-expanded={suggestionsOpen} aria-controls="recipient-suggestions" autoComplete="off" />
								</div>
								<p className="mt-1.5 text-xs text-muted-foreground">{t("notificationCampaigns.searchRecipientHint")}</p>
								{suggestionsOpen && <div id="recipient-suggestions" role="listbox" className="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg">
										{recipients.isFetching && <p className="px-3 py-4 text-sm text-muted-foreground">{t("notificationCampaigns.searchingRecipients")}</p>}
										{!recipients.isFetching && recipients.data?.items.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground">{t("notificationCampaigns.noRecipientsFound")}</p>}
										{recipients.data?.items.map(recipient_5 => {
                const selected = selectedIds.has(recipient_5.id);
                return <button type="button" role="option" aria-selected={selected} key={recipient_5.id} onMouseDown={event_1 => event_1.preventDefault()} onClick={() => addRecipient(recipient_5)} disabled={selected} className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left hover:bg-muted disabled:opacity-60">
													<span className="min-w-0"><span className="block truncate text-sm font-medium">{recipient_5.name}</span><span className="block truncate text-xs text-muted-foreground">{recipient_5.email} · {t(`roles.${recipient_5.role}`, {
                        defaultValue: recipient_5.role
                      })}</span></span>
													{selected && <Check className="size-4 shrink-0 text-primary" />}
												</button>;
              })}
									</div>}
							</div>

							<div className="grid gap-2">
								<div className="flex items-end justify-between gap-3"><div><h3 className="font-medium">{t("notificationCampaigns.selectedRecipients", {
                    count: selectedRecipients.length
                  })}</h3><p className="text-xs text-muted-foreground">{t("notificationCampaigns.individualBatchHint")}</p></div></div>
								<div className="overflow-hidden rounded-lg border">
									<Table>
										<TableHeader className="bg-muted/40"><TableRow><TableHead>{t("notificationCampaigns.recipientName")}</TableHead><TableHead>{t("notificationCampaigns.recipientEmail")}</TableHead><TableHead>{t("notificationCampaigns.recipientRole")}</TableHead><TableHead className="w-20 text-right">{t("notificationCampaigns.actions")}</TableHead></TableRow></TableHeader>
										<TableBody>
											{selectedRows.length === 0 && <TableRow><TableCell colSpan={4} className="h-20 text-center text-muted-foreground">{t("notificationCampaigns.noSelectedRecipients")}</TableCell></TableRow>}
											{selectedRows.map(recipient_6 => <TableRow key={recipient_6.id}>
													<TableCell className="font-medium">{recipient_6.name}</TableCell>
													<TableCell className="text-muted-foreground">{recipient_6.email}</TableCell>
													<TableCell><Badge variant="secondary">{t(`roles.${recipient_6.role}`, {
                          defaultValue: recipient_6.role
                        })}</Badge></TableCell>
													<TableCell className="text-right"><Button type="button" variant="ghost" size="icon" onClick={() => removeRecipient(recipient_6.id)} aria-label={t("notificationCampaigns.removeRecipient", {
                        name: recipient_6.name
                      })}><Trash2 className="size-4" /></Button></TableCell>
												</TableRow>)}
										</TableBody>
									</Table>
								</div>
								{selectedRecipients.length > SELECTED_PAGE_SIZE && <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
										<Button type="button" variant="outline" size="icon" className="size-8" disabled={selectedPage <= 1} onClick={() => setSelectedPage(page => page - 1)} aria-label={t("pagination.prev")}><ChevronLeft className="size-4" /></Button>
										<span className="tabular-nums">{t("pagination.page", {
                  page: selectedPage,
                  pageCount: selectedPageCount
                })}</span>
										<Button type="button" variant="outline" size="icon" className="size-8" disabled={selectedPage >= selectedPageCount} onClick={() => setSelectedPage(page_0 => page_0 + 1)} aria-label={t("pagination.next")}><ChevronRight className="size-4" /></Button>
									</div>}
							</div>
						</div>}
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="gap-1"><CardTitle>{t("notificationCampaigns.content")}</CardTitle><p className="text-sm text-muted-foreground">{t("notificationCampaigns.contentHint")}</p></CardHeader>
				<CardContent className="grid gap-5">
					<div className="grid gap-3 sm:grid-cols-2">
						<div className="grid gap-1.5"><Label htmlFor="campaign-category">{t("notificationCampaigns.category")}</Label><select id="campaign-category" className="h-10 rounded-md border bg-background px-3 text-sm" value={form.category} onChange={event_2 => set("category", event_2.target.value as FormState["category"])}>{CATEGORIES.map(value_0 => <option key={value_0} value={value_0}>{t(`notificationCampaigns.categories.${value_0}`)}</option>)}</select></div>
						<div className="grid gap-1.5"><Label htmlFor="campaign-action">{t("notificationCampaigns.action")}</Label><select id="campaign-action" className="h-10 rounded-md border bg-background px-3 text-sm" value={form.actionKind} onChange={event_3 => set("actionKind", event_3.target.value as FormState["actionKind"])}>{ACTION_KINDS.map(value_1 => <option key={value_1} value={value_1}>{t(`notificationCampaigns.actionsList.${value_1}`)}</option>)}</select></div>
					</div>
					{!["none", "profile"].includes(form.actionKind) && <div className="grid gap-1.5"><Label htmlFor="campaign-action-target">{t("notificationCampaigns.actionTarget")}</Label><Input id="campaign-action-target" value={form.actionTarget} onChange={event_4 => set("actionTarget", event_4.target.value)} /></div>}

					{/* Площадка работает только на русском (решение заказчика 26.09.2026):
         вкладки переводов прячем, пока языков больше одного не станет. */}
					{EDITABLE_LOCALES.length > 1 && <div className="flex flex-wrap gap-1 rounded-lg bg-muted p-1" role="tablist" aria-label={t("notificationCampaigns.languages")}>
						{EDITABLE_LOCALES.map(locale => {
            const translated = Boolean(form[CONTENT_FIELDS[locale].title] && form[CONTENT_FIELDS[locale].body]);
            return <button type="button" role="tab" aria-selected={activeLocale === locale} key={locale} onClick={() => setActiveLocale(locale)} className={`flex min-h-10 items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors ${activeLocale === locale ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}><span>{t(`notificationCampaigns.languagesList.${locale}`)}</span>{locale === "ru" ? <Badge variant="secondary">{t("notificationCampaigns.required")}</Badge> : translated ? <Check className="size-3.5 text-primary" /> : <span className="text-xs">{t("notificationCampaigns.fallbackShort")}</span>}</button>;
          })}
					</div>}

					<div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
						<div className="grid gap-4">
							{EDITABLE_LOCALES.length > 1 && <div className="flex flex-wrap items-center justify-between gap-2">
								<div><h3 className="font-medium">{t(`notificationCampaigns.languagesList.${activeLocale}`)}</h3><p className="text-xs text-muted-foreground">{activeLocale === "ru" ? t("notificationCampaigns.russianRequiredHint") : t("notificationCampaigns.translationFallbackHint")}</p></div>
								{activeLocale !== "ru" && <div className="flex gap-1"><Button type="button" variant="outline" size="sm" onClick={copyRussian}><Copy className="size-3.5" />{t("notificationCampaigns.copyRussian")}</Button><Button type="button" variant="ghost" size="sm" onClick={clearTranslation}>{t("notificationCampaigns.clearTranslation")}</Button></div>}
							</div>}
							<div className="grid gap-1.5"><span className="flex items-center justify-between gap-2"><Label htmlFor={`campaign-title-${activeLocale}`}>{t("notificationCampaigns.messageTitle")}</Label><span className="text-xs tabular-nums text-muted-foreground">{activeTitle.length}/160</span></span><Input id={`campaign-title-${activeLocale}`} maxLength={160} value={activeTitle} onChange={event_5 => set(fields.title, event_5.target.value)} placeholder={t("notificationCampaigns.messageTitlePlaceholder")} /></div>
							<div className="grid gap-1.5"><span className="flex items-center justify-between gap-2"><Label htmlFor={`campaign-body-${activeLocale}`}>{t("notificationCampaigns.messageBody")}</Label><span className="text-xs tabular-nums text-muted-foreground">{activeBody.length}/5000</span></span><textarea id={`campaign-body-${activeLocale}`} maxLength={5000} rows={8} className="min-h-44 resize-y rounded-md border bg-background px-3 py-2 text-sm leading-6 outline-none focus-visible:ring-2 focus-visible:ring-ring" value={activeBody} onChange={event_6 => set(fields.body, event_6.target.value)} placeholder={t("notificationCampaigns.messageBodyPlaceholder")} /></div>
						</div>
						<aside className="rounded-xl border bg-muted/30 p-4"><p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("notificationCampaigns.notificationPreview")}</p><div className="rounded-xl border bg-background p-4 shadow-sm"><div className="flex gap-3"><span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-semibold text-primary">O</span><div className="min-w-0"><p className="font-medium leading-5">{previewTitle}</p><p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-muted-foreground">{previewBody}</p><p className="mt-2 text-xs text-muted-foreground">{t("notificationCampaigns.previewNow")}</p></div></div></div>{activeLocale !== "ru" && (!activeTitle || !activeBody) && <p className="mt-3 text-xs text-muted-foreground">{t("notificationCampaigns.previewUsingRussian")}</p>}</aside>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="gap-1"><CardTitle>{t("notificationCampaigns.scheduleTitle")}</CardTitle><p className="text-sm text-muted-foreground">{t("notificationCampaigns.scheduleHint")}</p></CardHeader>
				<CardContent className="grid gap-4">
					<div className="grid gap-2 sm:grid-cols-2">
						{(["now", "scheduled"] as const).map(mode => <label key={mode} className={`flex min-h-12 cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${form.schedule === mode ? "border-primary bg-primary/5" : "hover:bg-muted/40"}`}>
								<input type="radio" name="schedule" checked={form.schedule === mode} onChange={() => set("schedule", mode)} />
								<span className="flex items-center gap-2 text-sm font-medium">{mode === "scheduled" && <Clock className="size-4 text-muted-foreground" />}{t(`notificationCampaigns.scheduleModes.${mode}`)}</span>
							</label>)}
					</div>
					{form.schedule === "scheduled" && <div className="grid gap-1.5 sm:max-w-sm">
							<Label htmlFor="campaign-scheduled-at">{t("notificationCampaigns.scheduleAt")}</Label>
							<Input id="campaign-scheduled-at" type="datetime-local" min={mskNowLocal()} value={form.scheduledAt} onChange={event_7 => set("scheduledAt", event_7.target.value)} />
							{form.scheduledAt && !scheduleInFuture && <p className="text-sm text-destructive">{t("notificationCampaigns.schedulePast")}</p>}
							{scheduleInFuture && <p className="text-sm text-muted-foreground">{t("notificationCampaigns.scheduledFor", {
              when: formatMskLabel(form.scheduledAt)
            })}</p>}
						</div>}
				</CardContent>
			</Card>

			<div className="flex flex-wrap items-center gap-2">
				<Button variant="outline" disabled={!valid || busy || drafts.length > 0} onClick={() => void createDrafts()}>{t("notificationCampaigns.saveDraft")}</Button>
				<Button disabled={!valid || busy} onClick={() => void runPreview()}>{form.schedule === "scheduled" ? t("notificationCampaigns.previewSchedule") : t("notificationCampaigns.preview")}</Button>
				{drafts.length > 0 && previews.length === 0 && <span className="text-sm text-muted-foreground">{t("notificationCampaigns.draftsPrepared", {
          count: drafts.length
        })}</span>}
			</div>

			<ConfirmDialog open={confirmOpen && previews.length > 0} title={form.schedule === "scheduled" ? t("notificationCampaigns.confirmSchedule") : t("notificationCampaigns.confirmSend")} description={form.schedule === "scheduled" ? t("notificationCampaigns.confirmScheduleDescription") : t("notificationCampaigns.confirmDescription")} confirmLabel={form.schedule === "scheduled" ? t("notificationCampaigns.approveSchedule") : t("notificationCampaigns.approve")} cancelLabel={t("notificationCampaigns.close")} confirmVariant="destructive" busy={actions.send.isPending} onClose={() => setConfirmOpen(false)} onConfirm={() => void send()}>
				<div className="grid gap-4">
					<div className="rounded-lg border bg-muted/30 p-4">
						<p className="font-semibold">{previews[0]?.preview.content.title}</p>
						<p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{previews[0]?.preview.content.body}</p>
					</div>
					{scheduleInFuture && <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
							<span className="flex items-center gap-1.5 text-muted-foreground"><Clock className="size-3.5" />{t("notificationCampaigns.scheduled")}</span>
							<span className="font-medium text-amber-700 dark:text-amber-500">{formatMskLabel(form.scheduledAt)}</span>
						</div>}
					<div className="flex items-center justify-between gap-4 rounded-lg border p-3 text-sm">
						<span className="text-muted-foreground">{t("notificationCampaigns.audience")}</span>
						<span className="font-medium">{t(`notificationCampaigns.audiences.${form.audience}`)}</span>
					</div>
					<div className="flex items-center justify-between gap-4 rounded-lg border p-3 text-sm">
						<span className="text-muted-foreground">{t("notificationCampaigns.targeted")}</span>
						<span className="font-semibold tabular-nums">{eligibleCount}</span>
					</div>
					{error && <p role="alert" className="text-sm text-destructive">{error}</p>}
				</div>
			</ConfirmDialog>
		</div>;
}